import React, { FC, RefObject, useCallback, useEffect, useState } from 'react';
import { Collapse } from 'bootstrap';
import { ProductCard, ProductCardProps } from '@components/molecules/ProductCard';
import { Container } from '@components/atoms/Container';
import { Pagination } from '@components/molecules/Pagination';
import { ServiceCard, ServiceCardProps } from '@components/molecules/ServiceCard';
import { ResultsType } from '@types';
import { ResultsPageTop, ResultsPageTopProps } from './ResultsPageTop';
import { SearchResultsHeader } from './SearchResultsHeader';
import styles from './ResultsContainer.module.css';
import { SITE_URLS } from '@constants';

interface ResultsContainerProps {
    type: ResultsType;
    results: ProductCardProps[] | ServiceCardProps[];
    scrollObserverRef?: RefObject<HTMLDivElement> | undefined;
    filters: JSX.Element;
    total: number;
    order: ResultsPageTopProps['order'];
    searchQuery?: string;
    loading: boolean;
}

export const ResultsContainer: FC<ResultsContainerProps> = ({
    type,
    results,
    scrollObserverRef,
    filters,
    total,
    order,
    searchQuery,
    loading,
}) => {
    const [sidebar, setSidebar] = useState<Collapse | null>(null);

    useEffect(() => {
        // destroy the element when the component unmounts
        return () => {
            try {
                sidebar?.dispose();
            } catch (e) {
                console.log('catch error disposing of sidebar');
            }
        };
    }, [sidebar]);

    const sidebarRef = useCallback((node: HTMLDivElement) => {
        if (node !== null) {
            const collapseInstance = Collapse.getOrCreateInstance(node, { toggle: false });
            setSidebar(collapseInstance);
        }
    }, []);

    const closeSidebar = () => {
        try {
            sidebar?.hide();
        } catch (e) {
            console.log('catch error closing sidebar');
        }
    };

    return (
        <div data-testid="results-container">
            <Container>
                <>
                    {searchQuery && <SearchResultsHeader searchQuery={searchQuery} />}
                    <div className="d-flex gap-4">
                        <div ref={sidebarRef} className={`${styles['collapseFilters']} collapse collapse-horizontal`}>
                            <div className={styles.background}></div>
                            <div className={styles.content}>
                                {/* close menu - visible on mobile only */}
                                <div className="sidebar-close d-lg-none w-100">
                                    <Container>
                                        <button onClick={() => closeSidebar()}>
                                            <i className="fa-light fa-xmark"></i>
                                        </button>
                                    </Container>
                                </div>
                                <Container classes={styles['filters-container']}>{filters}</Container>
                                <div className="d-none d-lg-block sidebar-vertical-separator"></div>
                            </div>
                        </div>
                        <div className="w-100 flex-grow-1">
                            <ResultsPageTop type={type} total={total} order={order} sidebar={sidebar} />
                            <div className="row row-gap-4 pt-3">
                                {type === ResultsType.products
                                    ? // products
                                      (results as ProductCardProps[]).map((item) => (
                                          <div key={`result-${item.uuid}`} className="d-grid col-6 col-md-4">
                                              <ProductCard {...item} />
                                          </div>
                                      ))
                                    : // merchants
                                      (results as ServiceCardProps[]).map((item) => (
                                          <div key={`result-${item.uuid}`} className="col-12 col-sm-6 col-lg-4">
                                              <ServiceCard
                                                  {...({
                                                      ...item,
                                                      link:
                                                          SITE_URLS.merchantDetail.replace(':uuid', `${item.uuid}`) +
                                                          `${type === ResultsType.services ? '?services' : ''}`,
                                                  } as ServiceCardProps)}
                                              />
                                          </div>
                                      ))}
                                <div ref={scrollObserverRef} style={{ height: '1px' }} />
                            </div>
                            <Pagination loading={loading} type={type} />
                        </div>
                    </div>
                </>
            </Container>
        </div>
    );
};
